import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { ChevronDown, Loader2, RefreshCw, UserPlus, Users } from "lucide-react";
import { API } from "../../apiBase";
import { PROFESSIONS } from "../../lib/professions";

const simplifier = (x) => String(x || "").toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "")
  .replace(/\b(d'|de |du |des )/g, "").replace(/[^a-z]/g, "");
const ALIAS = { opticien: "Opticien-lunetier", infirmierdiplomeetat: "Infirmier", kinesitherapeute: "Masseur-kinésithérapeute",
  manipulateurelectroradiologiemedicale: "Manipulateur d'ERM", dentiste: "Chirurgien-dentiste", podologue: "Pédicure-podologue" };
export const professionConnue = (x) => {
  if (!x) return "";
  const k = simplifier(x);
  return PROFESSIONS.find((p) => simplifier(p) === k) || ALIAS[k] || x;
};
export const prenomPropre = (x) => String(x || "").trim().toLowerCase()
  .replace(/(^|[\s\-'’])(\p{L})/gu, (m, a, b) => a + b.toUpperCase());
const CARTE = { valide: "CPS valide", expiree: "CPS expirée", opposition: "CPS en opposition", absente: "Sans carte physique", inconnue: "Carte non vérifiée" };

/**
 * Professionnels rattachés à l'établissement dans l'Annuaire santé, proposés à l'ajout.
 * Un clic préremplit le formulaire (nom, prénom, RPPS, profession) : l'utilisateur complète l'e-mail.
 */
export default function ProsAnnuaireSuggestions({ structureId, onChoisir }) {
  const [etat, setEtat] = useState({ etat: "encours", items: [] });
  const [ouvert, setOuvert] = useState(false);
  const [cherche, setCherche] = useState("");
  const [essai, setEssai] = useState(0);

  useEffect(() => {
    let vivant = true;
    (async () => {
      setEtat({ etat: "encours", items: [] });
      try {
        const [{ data: s }, { data: users }] = await Promise.all([
          axios.get(`${API}/pro/structures/${structureId}`),
          axios.get(`${API}/pro/structures/${structureId}/users`),
        ]);
        const ids = (s.fhir_ids || []).join(",");
        if (!ids) { if (vivant) setEtat({ etat: "sans", items: [] }); return; }
        const deja = new Set((users || []).filter((u) => u.id_type === "RPPS").map((u) => u.professional_id));
        const { data } = await axios.get(`${API}/mss-commande/annuaire/etablissement-professionnels`, { params: { ids }, timeout: 30000 });
        if (vivant) setEtat({ etat: "ok", items: (data.professionnels || []).filter((p) => !deja.has(p.rpps)), tronque: data.tronque });
      } catch (e) {
        if (vivant) setEtat({ etat: "erreur", items: [] });
      }
    })();
    return () => { vivant = false; };
  }, [structureId, essai]);

  const visibles = useMemo(() => {
    const f = simplifier(cherche);
    return f ? etat.items.filter((p) => simplifier(`${p.nom} ${p.prenom} ${p.profession || ""} ${p.rpps}`).includes(f)) : etat.items;
  }, [etat.items, cherche]);

  if (etat.etat === "sans" || (etat.etat === "ok" && !etat.items.length)) return null;

  return (
    <div className="rounded-lg border border-sky-200 bg-white" data-testid="pros-annuaire">
      <button type="button" onClick={() => setOuvert((o) => !o)} aria-expanded={ouvert}
        className="w-full flex items-center justify-between gap-2 px-3.5 py-2.5 text-left">
        <span className="inline-flex items-center gap-2 text-[12.5px] text-[#0F2B5B]">
          <Users size={14} className="text-[#0369A1]" />
          {etat.etat === "encours" && <><Loader2 size={12} className="animate-spin" /> Recherche des professionnels de l'établissement…</>}
          {etat.etat === "ok" && <><strong>{etat.items.length}</strong> professionnel{etat.items.length > 1 ? "s" : ""} de l'établissement dans l'Annuaire santé : choisissez pour préremplir</>}
          {etat.etat === "erreur" && "La liste de l'Annuaire santé n'a pas pu être chargée."}
        </span>
        {etat.etat === "erreur"
          ? <span role="button" tabIndex={0} onClick={(e) => { e.stopPropagation(); setEssai((n) => n + 1); }}
              className="inline-flex items-center gap-1 text-[12px] text-[#0369A1]"><RefreshCw size={12} /> Réessayer</span>
          : etat.etat === "ok" && <ChevronDown size={15} className={`text-[#475569] transition-transform ${ouvert ? "rotate-180" : ""}`} />}
      </button>
      {ouvert && etat.etat === "ok" && (
        <div className="border-t border-sky-100 px-3.5 py-3 grid gap-2">
          {etat.items.length > 8 && (
            <input className="mc-input py-1.5 text-[12.5px]" value={cherche} onChange={(e) => setCherche(e.target.value)}
              placeholder="Rechercher par nom, profession ou RPPS" aria-label="Rechercher un professionnel" data-testid="pros-annuaire-search" />
          )}
          <ul className="grid gap-1 max-h-64 overflow-y-auto pr-1">
            {visibles.map((p) => (
              <li key={p.rpps}>
                <button type="button" data-testid={`pros-annuaire-${p.rpps}`}
                  onClick={() => { onChoisir({ ...p, prenom: prenomPropre(p.prenom), profession: professionConnue(p.profession) }); setOuvert(false); }}
                  className="w-full text-left rounded-md px-2.5 py-2 hover:bg-sky-50 flex items-center gap-3">
                  <UserPlus size={14} className="text-[#0369A1] shrink-0" />
                  <span className="flex-1 min-w-0 text-[12.5px]">
                    <span className="font-medium text-[#0F2B5B]">{p.nom} {prenomPropre(p.prenom)}</span>
                    <span className="text-[#64748B]"> · {professionConnue(p.profession) || "profession à préciser"} · <span className="font-mono">RPPS {p.rpps}</span></span>
                  </span>
                  <span className="text-[11px] text-[#64748B] shrink-0">{CARTE[p.carte] || ""}</span>
                </button>
              </li>
            ))}
            {!visibles.length && <li className="text-[12.5px] text-[#64748B] px-2">Aucun professionnel ne correspond à « {cherche} ».</li>}
          </ul>
          {etat.tronque && <p className="text-[11.5px] text-[#64748B]">Seuls les 100 premiers professionnels sont proposés : ajoutez les autres avec leur numéro RPPS.</p>}
        </div>
      )}
    </div>
  );
}
